/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useCurrentVersionedAppId } from 'src/luis/modules/apps/hooks/useCurrentVersionedAppId';
import { UtteranceIntent } from 'src/luis/projects/analytics/types/UtteranceIntent';
import { ModelIdentifier } from 'src/luis/projects/utterances/handlers/utteranceHandlerGroup';
import { UtterancePredictionsStore } from 'src/luis/projects/utterances/hooks/useUtterances';
import { UtteranceWithAnalytics } from 'src/luis/projects/utterances/types/UtteranceWithAnalytics';

/**
 * Maps the utterances with their analytics results
 * and merge them in one type.
 */
export const useUtterancesWithAnalytics = ({
    model,
    isLoading,
    utterances: utterancesWithoutAnalytics
}: ModelIdentifier & { isLoading: boolean; utterances: UtterancePredictionsStore[] }) => {
    const { dispatcher, rootStore } = useGlobalState();
    const { versionId } = useCurrentVersionedAppId();

    const { analyticsStore } = rootStore.currentLuisAppStore.currentAppVersionStore;

    React.useEffect(() => {
        if (!isLoading && (analyticsStore.serviceStatus === 'notStarted' || !analyticsStore.intentsTrainingResult.has(model.id))) {
            dispatcher.dispatch('loadFaultyIntent', { id: model.id });
        }
    }, [dispatcher.dispatch, model, isLoading, analyticsStore.serviceStatus]);

    const modelTrainingResult = analyticsStore.intentsTrainingResult.get(model.id);

    const utterances: UtteranceWithAnalytics[] = React.useMemo(
        () =>
            modelTrainingResult
                ? utterancesWithoutAnalytics.map<UtteranceWithAnalytics>(u => {
                      const utteranceId = u.utteranceStore.id;
                      let unclearIntents: UtteranceIntent[] = [];
                      let incorrectIntents: UtteranceIntent[] = [];

                      if (modelTrainingResult.incorrectUtterances.has(utteranceId)) {
                          unclearIntents = modelTrainingResult.incorrectUtterances.get(utteranceId).unclearIntents;
                          incorrectIntents = modelTrainingResult.incorrectUtterances.get(utteranceId).incorrectIntents;
                      } else if (modelTrainingResult.unclearUtterances.has(utteranceId)) {
                          unclearIntents = modelTrainingResult.unclearUtterances.get(utteranceId).unclearIntents;
                          incorrectIntents = modelTrainingResult.unclearUtterances.get(utteranceId).incorrectIntents;
                      }

                      return {
                          ...u,
                          analytics: {
                              unclearIntents,
                              incorrectIntents
                          }
                      };
                  })
                : utterancesWithoutAnalytics,
        [utterancesWithoutAnalytics, modelTrainingResult]
    );

    React.useEffect(() => {
        if (analyticsStore.serviceStatus === 'notStarted' || analyticsStore.serviceStatus === 'outdated') {
            dispatcher.dispatch('loadMetadata', { id: versionId });
        }
    }, [analyticsStore.serviceStatus]);

    return { utterances };
};
